import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    Output,
    Renderer2,
    OnInit,
    OnChanges,
    SimpleChanges,
    NgZone,
    ElementRef,
    ViewChild
} from '@angular/core';
import { ActionSheetController, ActionSheetOptions, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
    Translation,
    FavoriteTicket,
    TicketStatus,
    ApplicationFacade,
    TicketsService,
    ProductsFacade
} from '@ridango/components';
import {
    AppStartupService,
    AuthService,
    BaseComponent,
    CoreFacade,
    Language,
    NetworkService,
    MtbService,
    ValidityTimerData
} from '@ridango/core';
import { filter, map, shareReplay, startWith, takeUntil, catchError, switchMap } from 'rxjs/operators';
import { TicketContextualMenuComponent } from '../customer-account/cards/ticket-contextual-menu/ticket-contextual-menu.component';
import { EMPTY, interval, Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import {
    EnhancedFareEngineTicket,
    FarePrice,
    TicketPrice
} from '@ridango/components/lib/account-tickets-ngrx/models/ticket.model';
import Env from '@ridango/core/env';
import { FareContractStatus } from '@ridango/components/lib/account-tickets-ngrx/models/ticket-status.model';
import { CardType, ProductCode } from '@ridango/components/lib/purchase/purchase.model';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { DateCalculationService } from '../../services/date-calculation.service';
import { TicketActionsService, TicketCtx, TicketData } from '../../services/ticket-actions/ticket-actions.service';
import { TicketModalStateService } from '../../services/ticket-modal-state.service';
import { TicketBackdropHighlightService } from '../../services/ticket-backdrop-highlight.service';
import { Temporal } from '@js-temporal/polyfill';
import {
    resolveTicketProgressBarTimeTextPlacement,
    TicketProgressBarTimeTextPlacement
} from '../../enums/ticket-progress-indicator.enum';
import {
    fixedValidityRangeOptionsFromAppStartup,
    ridangoDateTimeValidityOptions,
    resolveFixedValidityRange,
    shouldUseDateTimeForQrTicket,
    useUnifiedTicketValidityDisplay
} from '../../services/ticket-validity-display.helper';

type TicketProgressIndicatorType = 'spinner' | 'bar';

@Component({
    selector: 'rwidget-ticket-card',
    templateUrl: './ticket-card.component.html',
    styleUrls: ['./ticket-card.component.scss']
})
export class TicketCardComponent extends BaseComponent implements OnDestroy, OnInit, OnChanges {
    @Input() ticket: EnhancedFareEngineTicket;
    @Input() isHistoryTicket = false;
    @Input() isRecent = false;
    @Input() favoriteTickets: FavoriteTicket[];
    @Input() active: boolean;
    @Input() startDateStr?: string;
    @Input() endDateStr?: string;
    @Input() recentlyUsed?: boolean;
    @Output() refreshTickets: EventEmitter<void> = new EventEmitter();
    @Output() nextStep: EventEmitter<void> = new EventEmitter<void>();

    public historyTicketStatus: string;
    public ProductCode = ProductCode;
    currency: string;
    dateformat: string;
    timezone: string;
    dateTimeFormat: string;
    readonly RETURN_TICKET = 'RETURN_TICKET';
    readonly BUY_TICKET_AGAIN = 'BUY_TICKET_AGAIN';
    readonly REMOVE_HISTORY_TICKET = 'REMOVE_HISTORY_TICKET';
    readonly AWAITING_REACTIVATION = 'AWAITING_REACTIVATION';
    readonly SEND_TICKET = 'SEND_TICKET';
    readonly LEND_TICKET = 'LEND_TICKET';
    readonly ADD_TO_FAVORITE_TICKET = 'ADD_TO_FAVORITE_TICKET';
    readonly TEN_TRIP_TICKET_UNITS = 10;
    readonly fareContractStatus = FareContractStatus;
    readonly ticketStatus = TicketStatus;
    primaryDateTimeFormat: string;
    private productsRequested = false;
    private latestProducts: any[] = [];
    /**
     * Determines if ticket should display full datetime format instead of date-only format.
     * Delegates to shared helper function.
     */
    protected shouldUseDateTimeForQrTicket = shouldUseDateTimeForQrTicket;
    private readonly ACTIONS = 'Actions';
    private readonly ACTIONS_SHEET = `${this.ACTIONS} sheet`;
    assetsPath$ = this.applicationFacade.assetsPath$;
    public cardType = CardType;
    public isOnline$ = this.networkService.getStatus();
    adjustedEndingDate$: Observable<string>;
    public ticketTimer$: Observable<ValidityTimerData | null>;
    public isSingleTicketGroup: boolean;
    public travellers: string;
    protected readonly FareContractStatus = FareContractStatus;
    public isActionsModalOpen = false;
    public isPartiallyUsedMultiUnitTicket = false;

    @ViewChild('ticketCardWrapper', { read: ElementRef }) ticketCardWrapper: ElementRef;

    constructor(
        private readonly appStartupService: AppStartupService,
        @Inject('ENV') public env: Env,
        public translate: TranslateService,
        private coreFacade: CoreFacade,
        private actionSheetCtrl: ActionSheetController,
        private popoverCtrl: PopoverController,
        private router: Router,
        private authService: AuthService,
        private applicationFacade: ApplicationFacade,
        private networkService: NetworkService,
        private renderer: Renderer2,
        private analyticsService: AnalyticsService,
        private dateCalculationService: DateCalculationService,
        private zone: NgZone,
        private ticketActionsService: TicketActionsService,
        private ticketsService: TicketsService,
        private ticketModalStateService: TicketModalStateService,
        private mtbService: MtbService,
        private productsFacade: ProductsFacade,
        private backdropHighlight: TicketBackdropHighlightService
    ) {
        super();
        this.currency = appStartupService.getCurrency();
        this.timezone = appStartupService.getTimezone();
        this.dateTimeFormat = env.PERIOD_TICKET_DATE_FORMAT;
        this.primaryDateTimeFormat = env.PRIMARY_DATE_TIME_FORMAT || this.dateTimeFormat;
    }

    ngOnInit(): void {
        // Fetch products if not loaded, needed for type-based datetime formatting
        this.productsFacade.products$.pipe(takeUntil(this.componentDestroyed$)).subscribe((products) => {
            if (!this.productsRequested && products.length === 0) {
                this.productsRequested = true;
                this.productsFacade.fetchPurchaseProducts(this.env.CHANNEL);
            }

            // Store products and enrich current ticket
            if (products.length > 0) {
                this.latestProducts = products;
                this.enrichTicketWithProduct();
            }
        });

        this.historyTicketStatus = this.setHistoryTicketStatus();
        this.updateAdjustedEndingDate();
        this.isSingleTicketGroup = !!this.ticket?.userProfiles?.[0]?.group;
        this.travellers = this.isSingleTicketGroup
            ? this.ticketsService.getSelectedTravellerTypesOnTicket(
                  this.ticket.userProfiles?.[0]?.companionProfiles,
                  this.ticket.dynamicProductParameters
              )
            : this.translateString(this.ticket?.userProfiles?.[0] ?? '');

        if (this.isTicketActive) {
            this.ticketTimer$ = interval(60000).pipe(
                takeUntil(this.componentDestroyed$),
                startWith(0),
                map(() => this.calculateTicketTimer()),
                shareReplay(1)
            );
        } else {
            this.ticketTimer$ = of(null);
        }
        this.isOnline$
            .pipe(
                switchMap((online) => {
                    if (online && this.env?.IS_NEW_AFC) {
                        return this.mtbService.getDeviceKey().pipe(catchError(() => of(null)));
                    }
                    return EMPTY;
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe();
    }

    updateAdjustedEndingDate() {
        const ticket$ = interval(15000).pipe(
            takeUntil(this.componentDestroyed$),
            map(() => this.ticket),
            startWith(this.ticket),
            filter((t): t is EnhancedFareEngineTicket => !!t),
            shareReplay(1)
        );
        this.adjustedEndingDate$ = this.dateCalculationService.getAdjustedEndingDate$(ticket$, this.timezone);
    }

    get isTicketActive() {
        if (this.isHistoryTicket) {
            return false;
        }

        if (typeof this.active === 'boolean') {
            return this.active;
        }

        return this.ticket.activated || this.isTicketStatusActive();
    }

    get isTicketNotCurrentlyValid(): boolean {
        return this.ticket?.status === FareContractStatus.ACTIVE && this.ticket?.allowedActions?.validable === false;
    }

    get isTicketDisplayedAsActive(): boolean {
        return this.isTicketActive && !this.isTicketNotCurrentlyValid;
    }

    get showTicketProgressSpinner(): boolean {
        return this.isTicketProgressIndicatorVisible && this.ticketProgressIndicatorType === 'spinner';
    }

    get showTicketProgressBar(): boolean {
        return this.isTicketProgressIndicatorVisible && this.ticketProgressIndicatorType === 'bar';
    }

    get showTicketProgressBarTimeText(): boolean {
        return this.ticketProgressBarTimeTextPlacement !== 'hidden';
    }

    get isTicketProgressBarTimeTextOnRight(): boolean {
        return this.ticketProgressBarTimeTextPlacement === 'right';
    }

    get showTicketCardPrice(): boolean {
        if (this.env.TICKET_CARD_HIDE_PRICE || this.ticket.cardType === this.cardType.GLOBAL || this.isRecent) {
            return false;
        }

        if (
            this.env.TICKET_PROGRESS_INDICATOR?.TICKET_CARD_HIDE_PRICE_FOR_ACTIVE === true &&
            this.isTicketDisplayedAsActive
        ) {
            return false;
        }

        return true;
    }

    private get isTicketProgressIndicatorVisible(): boolean {
        return (
            this.isTicketDisplayedAsActive &&
            !this.isRecent &&
            this.ticket?.cardType !== this.cardType.GLOBAL &&
            this.isTicketProgressIndicatorEnabled
        );
    }

    private get isTicketProgressIndicatorEnabled(): boolean {
        const indicatorConfig = this.env.TICKET_PROGRESS_INDICATOR;
        return Boolean(indicatorConfig) && indicatorConfig.SHOW_IN_TICKET_CARD !== false;
    }

    private get ticketProgressIndicatorType(): TicketProgressIndicatorType {
        const configuredType = this.env.TICKET_PROGRESS_INDICATOR?.TICKET_CARD_INDICATOR_TYPE;

        if (configuredType === 'bar' || configuredType === 'spinner') {
            return configuredType;
        }

        return 'spinner';
    }

    private get ticketProgressBarTimeTextPlacement(): TicketProgressBarTimeTextPlacement {
        const placement = this.env.TICKET_PROGRESS_INDICATOR?.TICKET_CARD_TIME_LABEL_PLACEMENT;

        return resolveTicketProgressBarTimeTextPlacement(placement);
    }

    private get isTicketCardHMinTimeFormatEnabled(): boolean {
        return this.env.TICKET_PROGRESS_INDICATOR?.TICKET_CARD_USE_H_MIN_FORMAT === true;
    }

    private isTicketStatusActive(): boolean {
        const status = this.ticket?.status;

        return (
            status === FareContractStatus.ACTIVE ||
            this.matchesStatus(status, FareContractStatus.CONSUMED) ||
            this.matchesStatus(status, TicketStatus.Active)
        );
    }

    get isTicketCanceled(): boolean {
        return this.matchesStatus(this.ticket.status, TicketStatus.Refunded, TicketStatus.CANCELLED);
    }

    get isTicketWithQRCode(): boolean {
        return this.ticket.cardType === 'Q' || this.ticket.cardType === 'S';
    }

    get isTicketRefunded(): boolean {
        return this.matchesStatus(this.ticket.status, TicketStatus.Returned);
    }

    get isTicketExpired(): boolean {
        return this.matchesStatus(this.ticket.status, TicketStatus.EXPIRED);
    }

    get isTicketPending(): boolean {
        const status = this.ticket?.status;

        return this.matchesStatus(status, TicketStatus.Pending, FareContractStatus.PENDING);
    }

    get isTicketAwaitingReactivation(): boolean {
        return this.matchesStatus(this.ticket.status, TicketStatus.AWAITING_REACTIVATION);
    }

    get isTicketPeriodPass(): boolean {
        return this.ticket.typeOfProduct?.code === ProductCode.PERIOD_PASS;
    }

    get isMultiTrip(): boolean {
        return this.ticket.typeOfProduct?.code === 'MULTI_TRIP';
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Re-enrich ticket when it changes (tickets refresh constantly)
        if (changes.ticket) {
            this.updateQrTicketFlags();
            if (this.latestProducts.length > 0) {
                this.enrichTicketWithProduct();
            }
        }
    }

    /**
     * Enriches ticket with product details from products store.
     * Matches by productId and adds product_type for datetime formatting.
     */
    private enrichTicketWithProduct(): void {
        if (!this.ticket || !this.latestProducts.length) {
            return;
        }

        // Only enrich if not already present
        if (!this.ticket.product_type) {
            const product = this.latestProducts.find((p) => p.product_id == this.ticket.productId);
            if (product?.product_type) {
                this.ticket.product_type = product.product_type;
                this.ticket.product = product;
            }
        }
    }

    private setHistoryTicketStatus(): string {
        if (this.isTicketCanceled) return 'TICKET_CANCELED';
        if (this.isTicketRefunded) return 'TICKET_REFUNDED';
        if (this.ticket.validated) return 'TICKET_USED';
        if (this.matchesStatus(this.ticket.status, TicketStatus.Changed) && this.ticket.units === 0)
            return 'TICKET_USED';
        return 'TICKET_EXPIRED';
    }

    private matchesStatus(status: unknown, ...candidates: string[]): boolean {
        if (typeof status !== 'string') {
            return false;
        }

        const normalizedStatus = status.trim();
        return candidates.some((candidate) => normalizedStatus === candidate.trim());
    }

    getProductTranslationOrDefault(): Observable<string> {
        if (this.env.APP_NAME === 'orebro' || this.env.APP_NAME === 'varmland') {
            return this.coreFacade.selectedLanguage$.pipe(
                map((lang: Language) => {
                    return this.ticket.names.translations[lang.code];
                })
            );
        } else {
            return this.coreFacade.selectedLanguage$.pipe(
                map((lang: Language) => {
                    const productTranslation = this.ticket.productTranslations.find(
                        (translation: Translation) => translation.lang === lang?.code
                    );
                    return productTranslation?.translation ?? this.ticket.productName;
                })
            );
        }
    }

    async presentActionSheet() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.TICKETS,
            name: this.ACTIONS
        });

        this.isActionsModalOpen = true;
        this.ticketModalStateService.setModalOpen(true);

        const actionSheetButtons = this.ticketActionsService.createActionsList(
            this.buildCtx(),
            this.ACTIONS_SHEET,
            'onSheet'
        );

        const options: ActionSheetOptions = {
            cssClass: 'ticket-actions',
            mode: 'ios',
            buttons: actionSheetButtons
        };

        const actionSheet = await this.actionSheetCtrl.create(options);

        // adds role=button to action sheet buttons for WCAG
        actionSheet.present().then(() => {
            document.querySelectorAll('.action-sheet-button').forEach((el) => {
                el.setAttribute('role', 'button');
            });
        });

        // Apply backdrop cutout immediately when backdrop appears
        this.backdropHighlight.highlight(this.ticketCardWrapper.nativeElement as HTMLElement);

        // start focus trap so screen readers won't read content behind the action sheet
        const containers = [
            document.querySelector('ion-app > ion-content'),
            document.querySelector('ion-app > ion-footer'),
            document.querySelector('ion-app > ion-modal#modal'),
            document.querySelector('head')
        ].filter((el) => el !== null) as HTMLElement[];

        containers.forEach((container) => {
            console.warn('set inert', container);
            this.renderer.setAttribute(container, 'inert', '');
        });

        await actionSheet.present();
        await actionSheet.onDidDismiss().then(() => {
            // end focus trap
            containers.forEach((container) => {
                this.renderer.removeAttribute(container, 'inert');
            });
            this.isActionsModalOpen = false;
            this.ticketModalStateService.setModalOpen(false);
            this.backdropHighlight.reset();
        });
    }

    async presentPopover(e: Event) {
        this.isActionsModalOpen = true;
        this.ticketModalStateService.setModalOpen(true);

        const actions = this.ticketActionsService.createActionsList(this.buildCtx(), this.ACTIONS_SHEET, 'onSheet');
        const popover = await this.popoverCtrl.create({
            component: TicketContextualMenuComponent,
            componentProps: {
                actions,
                mode: 'default'
            },
            mode: 'ios',
            event: e
        });

        await popover.present();
        this.backdropHighlight.highlight(this.ticketCardWrapper.nativeElement as HTMLElement);

        await popover.onDidDismiss().then(() => {
            this.backdropHighlight.reset();
            this.isActionsModalOpen = false;
            this.ticketModalStateService.setModalOpen(false);
        });
    }

    private isFavoriteTicket() {
        return this.favoriteTickets?.find((t) => t.productId === Number(this.ticket?.productId));
    }

    private buildCtx(): TicketCtx {
        return {
            ticket: this.ticket as TicketData,
            env: this.env,
            isHistory: this.isHistoryTicket,
            isAuth: this.authService.getLoginStatus(),
            locale: this.translate.currentLang,
            refresh: this.refreshTickets,
            isFavourite: !!this.isFavoriteTicket(),
            startDateStr: this.startDateStr,
            endDateStr: this.endDateStr
        };
    }

    private calculateTicketTimer(): ValidityTimerData | null {
        if (!this.isTicketActive) {
            return null;
        }

        const fallbackTimerData = this.getFallbackTimerData();
        const endingDate = this.getEndingDate();
        if (!endingDate) {
            return fallbackTimerData;
        }

        const endTime = new Date(endingDate);
        if (isNaN(endTime.getTime())) {
            return fallbackTimerData;
        }

        const now = new Date();
        const remainingMs = endTime.getTime() - now.getTime();

        if (remainingMs <= 0) {
            return {
                remainingTimeText: '00:00',
                progressValue: 0,
                isExpired: true
            };
        }

        const startDate = this.getStartingDate();
        if (!startDate) {
            return {
                ...fallbackTimerData,
                remainingTimeText: this.formatRemainingTime(remainingMs)
            };
        }

        const startTime = new Date(startDate);
        if (isNaN(startTime.getTime())) {
            return {
                ...fallbackTimerData,
                remainingTimeText: this.formatRemainingTime(remainingMs)
            };
        }

        const totalDurationMs = endTime.getTime() - startTime.getTime();
        if (totalDurationMs <= 0) {
            return {
                ...fallbackTimerData,
                remainingTimeText: this.formatRemainingTime(remainingMs)
            };
        }

        const progressValue = Math.min(Math.max((remainingMs / totalDurationMs) * 100, 0), 100);
        const remainingTimeText = this.formatRemainingTime(remainingMs);

        return {
            remainingTimeText,
            progressValue,
            isExpired: false
        };
    }

    private getFallbackTimerData(): ValidityTimerData {
        return {
            remainingTimeText: '--:--',
            progressValue: 100,
            isExpired: false
        };
    }

    private getStartingDate(): Date | string | undefined {
        return this.ticket?.starting || this.ticket?.startDate || this.ticket?.timestamps?.activationWindowStart;
    }

    get ticketPriceForDisplay(): number | null {
        if (this.ticket?.price != null) {
            return typeof this.ticket.price === 'object' ? this.ticket.price.amount : this.ticket.price;
        }

        if (this.ticket?.farePrice?.amount != null) {
            return this.ticket.farePrice.amount;
        }

        if (this.ticket?.basePrice != null) return this.ticket.basePrice;
        return null;
    }

    get ticketPriceCurrency(): string | null {
        const priceCurrency = this.getCurrencyFromPrice(this.ticket?.price);
        if (priceCurrency != null) {
            return String(priceCurrency);
        }

        return null;
    }

    get ticketFarePriceCurrency(): string | null {
        const farePriceCurrency = this.getCurrencyFromFarePrice(this.ticket?.farePrice);
        if (farePriceCurrency != null) {
            return String(farePriceCurrency);
        }

        return null;
    }

    private getCurrencyFromPrice(price?: TicketPrice): string | number | null {
        if (price && typeof price === 'object') {
            return price.currency ?? null;
        }

        return null;
    }

    private getCurrencyFromFarePrice(price?: FarePrice): string | number | null {
        if (price?.currency == null) {
            return null;
        }

        if (typeof price.currency === 'string') {
            return price.currency;
        }

        return price.currency.currencyCode ?? price.currency.numericCode ?? null;
    }

    private formatRemainingTime(remainingMs: number): string {
        const duration = Temporal.Duration.from({ milliseconds: remainingMs });
        const { days, hours, minutes } = duration.round({ largestUnit: 'day' });

        if (days > 0) {
            const dayLabel = days === 1 ? 'DAY' : 'DAYS';
            return `${days}\n${this.translate.instant(dayLabel)}`;
        }

        if (this.isTicketCardHMinTimeFormatEnabled) {
            return `${this.formatDurationUnit(hours, 'hour')} ${this.formatDurationUnit(minutes, 'minute')}`;
        }

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    private formatDurationUnit(value: number, unit: 'hour' | 'minute'): string {
        const locale = this.translate.currentLang || 'en';

        try {
            return new Intl.NumberFormat(locale, {
                style: 'unit',
                unit,
                unitDisplay: 'narrow'
            } as Intl.NumberFormatOptions).format(value);
        } catch {
            const fallbackSuffix = unit === 'hour' ? 'h' : 'min';
            return `${value.toString().padStart(2, '0')}${fallbackSuffix}`;
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    goToTicket(isRecent, ticket) {
        if (isRecent) {
            return;
        }

        this.router.navigate([this.translate.currentLang, 'account', 'tickets', ticket.docNum ?? ticket.id], {
            queryParams: {
                ...(this.isHistoryTicket && { isHistory: true }),
                ...(this.env.IS_MOBILE_APP && this.recentlyUsed && { recentlyUsed: true })
            }
        });
    }

    private getEndingDate(): Date | string | undefined {
        return this.fixedValidityRangeEnd(this.ticket) || this.ticket?.ending || this.ticket?.endDate;
    }

    translateString(inputObject) {
        if (!inputObject) return '-';
        if (Object.prototype.hasOwnProperty.call(inputObject, 'nameTranslations')) {
            const translations = inputObject.nameTranslations?.translations;
            if (!translations) return '-';

            return translations[this.translate.currentLang] || translations[Object.keys(translations)[0]] || '-';
        } else if (inputObject.name?.translations) {
            const translations = inputObject.name.translations;
            return translations[this.translate.currentLang] || translations[Object.keys(translations)[0]] || '-';
        } else {
            return '-';
        }
    }

    get useUnifiedTicketValidityDisplay(): boolean {
        return useUnifiedTicketValidityDisplay(this.env);
    }

    ridangoDateTimeOptions(ticket: EnhancedFareEngineTicket) {
        return ridangoDateTimeValidityOptions(this.env, ticket);
    }

    activeTicketRangeStart(ticket: EnhancedFareEngineTicket) {
        return this.fixedValidityRangeStart(ticket) || (this.env.IS_NEW_AFC ? ticket.startDate : ticket.starting);
    }

    bookletRangeStart(ticket: EnhancedFareEngineTicket) {
        const fixedRangeStart = this.fixedValidityRangeStart(ticket);
        if (fixedRangeStart) {
            return fixedRangeStart;
        }

        if (this.recentlyUsed) {
            return ticket.starting;
        }

        return ticket.timestamp || ticket.starting;
    }

    bookletRangeEnd(ticket: EnhancedFareEngineTicket) {
        return this.fixedValidityRangeEnd(ticket) || ticket.ending || ticket.bookletEnding || ticket.expiredAt;
    }

    hasFixedValidityRange(ticket: EnhancedFareEngineTicket): boolean {
        return Boolean(
            resolveFixedValidityRange(ticket, fixedValidityRangeOptionsFromAppStartup(this.appStartupService))
        );
    }

    fixedValidityRangeStart(ticket: EnhancedFareEngineTicket): Date | undefined {
        return resolveFixedValidityRange(ticket, fixedValidityRangeOptionsFromAppStartup(this.appStartupService))
            ?.start;
    }

    fixedValidityRangeEnd(ticket: EnhancedFareEngineTicket): Date | undefined {
        return resolveFixedValidityRange(ticket, fixedValidityRangeOptionsFromAppStartup(this.appStartupService))?.end;
    }

    private updateQrTicketFlags(): void {
        this.isPartiallyUsedMultiUnitTicket = this.calculateIsPartiallyUsedMultiUnitTicket(this.ticket);
    }

    private calculateIsPartiallyUsedMultiUnitTicket(ticket?: EnhancedFareEngineTicket): boolean {
        if (!ticket || ticket.initialUnits == null || ticket.units == null) {
            return false;
        }
        return ticket.initialUnits > 1 && ticket.units > 0 && ticket.units < ticket.initialUnits;
    }
}
